import { useFormik } from "formik";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import React from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { type TreeConfigurationEditDialogProps } from "./Config.interfaces";
import { type BlockTemporarilyRestartIfFailedContentConfigForm_BlockTemporarilyRestartIfFailedContentConfigFragment$key } from "../../../__generated__/BlockTemporarilyRestartIfFailedContentConfigForm_BlockTemporarilyRestartIfFailedContentConfigFragment.graphql";
import { type BlockTemporarilyRestartIfFailedContentConfigForm_EditMutation } from "../../../__generated__/BlockTemporarilyRestartIfFailedContentConfigForm_EditMutation.graphql";
import { type BlockTemporarilyRestartIfFailedContentConfigForm_TreeNodeFragment$key } from "../../../__generated__/BlockTemporarilyRestartIfFailedContentConfigForm_TreeNodeFragment.graphql";
import { ValidatedField } from "../../../components/ValidatedField";
import { CONFIGS_TRANSLATIONS } from "../../../translations/educational-offer";
import { useDraftStatusUpdater } from "./use-draft-status-updater";

const CONFIGURATION_FRAGMENT = graphql`
	fragment BlockTemporarilyRestartIfFailedContentConfigForm_BlockTemporarilyRestartIfFailedContentConfigFragment on BlockTemporarilyRestartIfFailedContentConfig {
		id
		configType
		daysToBlock
	}
`;

const TREE_NODE_FRAGMENT = graphql`
	fragment BlockTemporarilyRestartIfFailedContentConfigForm_TreeNodeFragment on TreeNode {
		id
	}
`;

const EDIT_CONFIGURATION_MUTATION = graphql`
	mutation BlockTemporarilyRestartIfFailedContentConfigForm_EditMutation(
		$input: EditBlockTemporarilyRestartIfFailedContentConfigInput!
	) {
		Admin {
			Tree {
				editBlockTemporarilyRestartIfFailedContentConfig(input: $input) {
					contentNode {
						...RestartIfFailedContentConfigEditor_TreeNodeFragment
					}
				}
			}
		}
	}
`;

type FormState = {
	daysToBlock: number;
};

type Props = {
	configurationFragmentRef: BlockTemporarilyRestartIfFailedContentConfigForm_BlockTemporarilyRestartIfFailedContentConfigFragment$key;
	treeNodeFragmentRef: BlockTemporarilyRestartIfFailedContentConfigForm_TreeNodeFragment$key;
	onBack?: () => void;
} & TreeConfigurationEditDialogProps;

export const BlockTemporarilyRestartIfFailedContentConfigForm = ({
	configurationFragmentRef,
	treeNodeFragmentRef,
	onBack,
}: Props) => {
	const { t } = useTranslation("common");
	const setDraft = useDraftStatusUpdater();
	const treeNode =
		useFragment<BlockTemporarilyRestartIfFailedContentConfigForm_TreeNodeFragment$key>(
			TREE_NODE_FRAGMENT,
			treeNodeFragmentRef,
		);
	const configuration =
		useFragment<BlockTemporarilyRestartIfFailedContentConfigForm_BlockTemporarilyRestartIfFailedContentConfigFragment$key>(
			CONFIGURATION_FRAGMENT,
			configurationFragmentRef,
		);
	const [edit, isEditing] =
		useMutation<BlockTemporarilyRestartIfFailedContentConfigForm_EditMutation>(
			EDIT_CONFIGURATION_MUTATION,
		);

	const formik = useFormik<FormState>({
		enableReinitialize: true,
		initialValues: {
			daysToBlock: configuration.daysToBlock,
		},
		onSubmit: (values, { setSubmitting }) => {
			edit({
				variables: {
					input: {
						contentNodeId: treeNode.id,
						configId: configuration.id,
						newDaysToBlock: values.daysToBlock,
					},
				},
				updater: setDraft,
			});
			setSubmitting(false);
			onBack!();
		},
	});

	return (
		<>
			<form onSubmit={formik.handleSubmit} className="p-fluid">
				<ValidatedField<FormState, number>
					name={"daysToBlock"}
					label={CONFIGS_TRANSLATIONS[configuration.configType]}
					component={({ fieldName, updateField, fieldValue }) => (
						<InputNumber
							name={fieldName}
							value={fieldValue}
							onChange={(e) => {
								updateField(e.value || undefined);
							}}
						/>
					)}
					formikConfig={formik}
				/>

				<Button
					disabled={Object.entries(formik.touched).length === 0 || isEditing}
					type="submit"
					label={t("actions.save")}
					className="mt-2"
				/>

				<Button
					type="button"
					onClick={() => {
						onBack!();
					}}
					label={t("actions.back")}
					className="p-button-secondary mt-2"
				/>
			</form>
		</>
	);
};
